import { useCallback, useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Mail, Send, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";

// components
import { Button } from "@components/button/button";
import { LoadingFinalizeExpedition } from "@components/loadings-skelleton/loading-finalize-expedition";
import { SpinnerSimple } from "@components/spinner-simple/spinner-simple";

import { DeliveryOptions } from "@pages/admin/gerenciar-clientes/components/opcao-entrega";
import { handleMensagensPorTipo } from "@pages/expedicao/dashboard/helpers/messagem-por-tipo";

import { useBuscarPorEnderecosPorTipo } from "@/hooks/enderecos/buscar-por-enderecos-por-tipo";
import { useLiberarRemessaBalcao } from "@/hooks/liberar-remessa";
import { useUpdateTarefa } from "@/hooks/minhas-tarefas/use-update-tarefa";
import { useStatusRemessa } from "@/hooks/remessas/status-remessa-do-cliente";
import { useBuscarTiposEntrega } from "@/hooks/tipos-entregas/buscar-tipos-entrega";

import { handleLabelTipoEntrega } from "@/utils/return-tipos-entrega";

// hooks / react-query

type FinalizeExpeditionModalProps = {
  remessa: any | null;
  typeDelivery: string;
  handleClose: () => void;
};

type TiposEntregaSelecionado = {
  id: number;
  tipo: string;
  endereco_entrega_id?: number | null;
};

const schema = z.object({
  observacao: z.string().min(10, "A mensagem deve ter no mínimo 10 caracteres."),
});

type FormData = z.infer<typeof schema>;

const FinalizeExpeditionModal = ({ handleClose, remessa }: FinalizeExpeditionModalProps) => {
  const queryClient = useQueryClient(); // ← necessário para invalidar a query

  const { enderecosTipos, isLoading: isLoadingEnderecos } = useBuscarPorEnderecosPorTipo(
    Number(remessa?.cliente_id)
  );

  // estado de carregamento inicial
  const isFetchingData = isLoadingEnderecos || !enderecosTipos;

  const { liberarRemessaAsync } = useLiberarRemessaBalcao();

  const { mutate: mutateUseUpdateTarefa, isPending } = useUpdateTarefa();
  const { statusRemessaAsync: solicitarRemessaAsync, isLoadingSolicitarRemessa } =
    useStatusRemessa();

  const { tiposEntrega } = useBuscarTiposEntrega();

  const [confirmarEnvio, setConfirmarEnvio] = useState(false);
  const [dataApi, setDataApi] = useState<any>({ observacao: null });
  const [outrosDetalhe, setOutrosDetalhe] = useState("");

  const [tiposEntregaSelecionado, setTiposEntregaSelecionado] =
    useState<TiposEntregaSelecionado | null>(null); // exemplo de tipo de entrega selecionado

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = useCallback(
    (data: FormData) => {
      if (!tiposEntregaSelecionado?.id) {
        toast.info("Verifique se você selecionou o tipo de entrega e tente novamente!", {
          position: "top-center",
        });
        return;
      }

      setDataApi(data);
      setConfirmarEnvio(true);
    },
    [tiposEntregaSelecionado]
  );

  const handleTipoEntregaChange = useCallback((tipoSelecionado: TiposEntregaSelecionado) => {
    setTiposEntregaSelecionado(tipoSelecionado);
    if (tipoSelecionado.tipo !== "outros") {
      setOutrosDetalhe("");
    }
  }, []);

  const confirmar = useCallback(async () => {
    const liberarRemessa = {
      remessa_id: remessa.remessa!,
      tipo_entrega_id: Number(tiposEntregaSelecionado?.id),
      observacao: dataApi?.observacao,
      outros:
        tiposEntregaSelecionado?.tipo === "outros" && outrosDetalhe.trim()
          ? outrosDetalhe.trim()
          : null,
    };

    // // Executa a ação de envio
    const { data } = await liberarRemessaAsync(liberarRemessa);

    if (data?.id) {
      mutateUseUpdateTarefa(
        {
          id_remessa: Number(remessa.remessa),
          payload: {
            status: "pedido_liberado", // confirmar status
            situacao: "pedido_liberado",
          },
        },
        {
          onSuccess: async () => {
            await solicitarRemessaAsync({
              id_remessa: remessa.remessa!,
              payload: { status: "pedido_liberado" },
            });

            await queryClient.invalidateQueries({ queryKey: ["expedicoes"] });

            setConfirmarEnvio(false);
            handleClose();

            return;
          },
        }
      );

      return;
    }
  }, [
    dataApi,
    handleClose,
    outrosDetalhe,
    tiposEntregaSelecionado,
    remessa,
    liberarRemessaAsync,
    mutateUseUpdateTarefa,
    solicitarRemessaAsync,
    queryClient,
  ]);

  /**
   */
  useEffect(() => {
    const enderecoDelivery = enderecosTipos?.residencial;
    const tipo = (tiposEntregaSelecionado?.tipo || "").toLowerCase().trim();
    // mesmo nome de quem recebe o e-mail (solicitante), não o do cliente
    const nome = remessa?.destinatario_email_nome ?? remessa?.cliente ?? "Cliente";
    const remessaId = remessa?.numero_remessa ?? 0;
    const endereco = `${enderecoDelivery?.logradouro}, N°${enderecoDelivery?.numero}, Bairro ${enderecoDelivery?.bairro}, CEP ${enderecoDelivery?.cep}.`;
    const responsavel = `${enderecoDelivery?.nome_responsavel}`;
    const setor = `${enderecoDelivery?.setor}`;

    if (handleMensagensPorTipo[tipo]) {
      const msg = handleMensagensPorTipo[tipo](nome, remessaId, endereco, responsavel, setor);
      setValue("observacao", msg, { shouldDirty: true, shouldValidate: true });
    }
  }, [tiposEntregaSelecionado, remessa, setValue, enderecosTipos]);

  useEffect(() => {
    if (!remessa) {
      return;
    }

    const tipoEntregaId = remessa?.tipo_entrega?.tipo_entrega_id;

    if (typeof tipoEntregaId !== "number" || !Number.isFinite(tipoEntregaId)) {
      setTiposEntregaSelecionado(null);
    } else {
      const tipoFromApi = tiposEntrega.find((item) => item.id === tipoEntregaId);

      if (tipoFromApi) {
        setTiposEntregaSelecionado({
          id: tipoFromApi.id,
          tipo: tipoFromApi.tipo,
        });
      } else {
        const tipo = handleLabelTipoEntrega(tipoEntregaId);
        setTiposEntregaSelecionado(tipo ? { id: tipoEntregaId, tipo } : null);
      }
    }

    setOutrosDetalhe("");
  }, [remessa, tiposEntrega]);

  return (
    <>
      {/* Modal principal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="max-h-[90vh] w-[700px] space-y-5 overflow-y-auto rounded-xl bg-neutral-50 px-6 py-5 shadow-shape">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="text-blue-600" size={24} />
              <h2 className="text-xl font-semibold text-zinc-800">Finalizar Expedição</h2>
            </div>
            <button onClick={handleClose}>
              <X className="size-5 text-zinc-400" />
            </button>
          </div>

          {isFetchingData ? (
            <LoadingFinalizeExpedition />
          ) : (
            <>
              {/* Formulário */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Mensagem do email */}
                <div>
                  <label className="mb-2 block text-zinc-800">Mensagem</label>
                  <textarea
                    {...register("observacao")}
                    rows={8}
                    placeholder="Digite a mensagem de liberação do pedido"
                    // className="w-full px-4 py-3 border border-zinc-800 rounded-lg resize-none outline-none"
                    className="w-full rounded-lg border border-zinc-800 px-4 py-3 text-zinc-700 outline-none"
                  />
                  {errors.observacao && (
                    <span className="text-sm text-red-500">{errors.observacao.message}</span>
                  )}
                </div>

                {/* Forma de envio */}
                <div className="mt-2 flex-row items-center rounded-md">
                  <DeliveryOptions
                    tiposEntrega={tiposEntrega}
                    tipoEntregaSelecionada={tiposEntregaSelecionado?.tipo ?? ""}
                    onTipoEntregaChange={handleTipoEntregaChange}
                    outrosTexto={outrosDetalhe}
                    onOutrosChange={setOutrosDetalhe}
                  />
                </div>

                <Button type="submit" className="mt-2 w-full">
                  {isLoadingSolicitarRemessa ? (
                    <SpinnerSimple />
                  ) : (
                    <>
                      <Send className="size-5" />
                      Salvar e Enviar
                    </>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Modal de Confirmação */}
      {confirmarEnvio && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="w-[400px] space-y-5 rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-zinc-800">
              Confirmar liberação da remessa {remessa?.numero_remessa}?
            </h3>
            <p className="text-sm text-zinc-600">
              Deseja realmente enviar está remessa para Recepção?
            </p>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                className="bg-zinc-300 text-zinc-800 hover:bg-zinc-400"
                onClick={() => setConfirmarEnvio(false)}
              >
                Cancelar
              </Button>
              <Button disabled={isPending} type="button" onClick={confirmar}>
                {isPending ? <SpinnerSimple /> : "Liberar remessa"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { FinalizeExpeditionModal };
